import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/base/buttons/button";
import { formatKcPayment } from "../../schemas/payment";

/**
 * UC-004 ActionCardKolikDlulis — prominent "Kolik dlužíš" Action Card pro
 * Milana/Zdeňka tenant dashboard (Phase 19, REQ-UI-006 + REQ-BIZ-017 +
 * REQ-PERF-004 mobile TTI < 3s — pre-fetched data je 1 round-trip).
 *
 * Layout (per UI_WIREFRAME mobile-first):
 *   - "Kolik dlužíš" small uppercase label
 *   - Velká částka (32px+)
 *   - Počet otevřených úhrad "X otevřených úhrad"
 *   - Primary CTA "Zaplatit nyní" → naviguje na poslední open payment
 *
 * Empty state — žádné otevřené úhrady → success message "Žádné otevřené
 * úhrady — díky!" (princip #5 Adoption — pozitivní feedback).
 *
 * Color-not-only (REQ-UI-018/022): empty state má ✓ symbol + zelenou.
 */

export interface ActionCardKolikDlulisProps {
  /** Suma otevřených úhrad v CZK (plain decimal string, žádný FX). */
  openTotalKc: string;
  openIntentCount: number;
  /** payment_intent_id nejbližšího open intent (CTA target). */
  nextIntentId: string | null;
  /** Pokud TRUE → render drill-down trigger button. */
  onDrillDown?: () => void;
}

export function ActionCardKolikDlulis(props: ActionCardKolikDlulisProps) {
  const { openTotalKc, openIntentCount, nextIntentId, onDrillDown } = props;
  const { t } = useTranslation("tenant");

  if (openIntentCount === 0) {
    return (
      <section
        className="flex flex-col gap-3 rounded-xl bg-primary p-5 shadow-xs ring-1 ring-secondary"
        data-testid="action-card-debt"
        data-state="empty"
      >
        <span className="text-sm font-semibold tracking-[0.04em] text-tertiary uppercase">
          {t("payment.actionCard.title")}
        </span>
        <span className="py-2 text-base text-success-primary" aria-live="polite">
          ✓ {t("payment.actionCard.empty")}
        </span>
      </section>
    );
  }

  return (
    <section
      className="flex flex-col gap-3 rounded-xl bg-primary p-5 shadow-xs ring-1 ring-secondary"
      data-testid="action-card-debt"
      data-state="open"
    >
      <span className="text-sm font-semibold tracking-[0.04em] text-tertiary uppercase">
        {t("payment.actionCard.title")}
      </span>
      <strong
        className="text-[2rem] leading-[1.1] font-bold text-primary"
        data-testid="action-card-debt-value"
      >
        {formatKcPayment(openTotalKc)}
      </strong>
      <span className="text-[0.9375rem] text-tertiary">
        {openIntentCount} × {t("payment.actionCard.openCount")}
      </span>
      <div className="mt-2">
        {nextIntentId ? (
          // TanStack type-safe Link stylovaný jako primary button (UUI utility).
          <Link
            to="/tenant/pay/$paymentId"
            params={{ paymentId: nextIntentId }}
            className="inline-flex items-center justify-center gap-1 rounded-lg bg-brand-solid px-3.5 py-2.5 text-sm font-semibold text-white shadow-xs-skeuomorphic ring-1 ring-transparent ring-inset outline-brand transition duration-100 ease-linear hover:bg-brand-solid_hover focus-visible:outline-2 focus-visible:outline-offset-2"
            data-testid="action-card-debt-cta"
          >
            {t("payment.actionCard.cta")}
          </Link>
        ) : null}
        {onDrillDown && (
          <Button
            color="tertiary"
            onPress={onDrillDown}
            data-testid="action-card-debt-drilldown"
          >
            {t("payment.actionCard.drillDownTitle")}
          </Button>
        )}
      </div>
    </section>
  );
}
